import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Plane, Star, Hotel } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import BookingModal from '../../components/BookingModal';

const Stays = () => {
  const { language } = useLanguage();
  const [stays, setStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedStay, setSelectedStay] = useState(null);

  useEffect(() => {
    fetchStays();
  }, []);

  // Fonction pour parser les dates françaises (ex: "5-7 Juin 2026")
  const parseFrenchDate = (dateStr) => {
    if (!dateStr) return new Date('2099-12-31');
    
    const months = {
      'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3,
      'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7,
      'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11,
      'january': 0, 'february': 1, 'march': 2, 'april': 3,
      'may': 4, 'june': 5, 'july': 6, 'august': 7,
      'september': 8, 'october': 9, 'november': 10, 'december': 11
    };
    
    const lower = dateStr.toLowerCase();
    let year = 2026;
    let month = 0;
    let day = 1;
    
    // Extraire l'année
    const yearMatch = dateStr.match(/20\d{2}/);
    if (yearMatch) year = parseInt(yearMatch[0]);
    
    // Extraire le mois
    for (const [monthName, monthIndex] of Object.entries(months)) {
      if (lower.includes(monthName)) {
        month = monthIndex;
        break;
      }
    }
    
    // Extraire le premier jour
    const dayMatch = dateStr.match(/(\d{1,2})/);
    if (dayMatch) day = parseInt(dayMatch[1]);
    
    return new Date(year, month, day);
  };

  const fetchStays = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/stays`);
      if (response.ok) {
        const data = await response.json();
        // Trier par date chronologique (du plus proche au plus loin)
        const sortedData = data.sort((a, b) => {
          const dateA = parseFrenchDate(a.date);
          const dateB = parseFrenchDate(b.date);
          return dateA - dateB;
        });
        setStays(sortedData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des séjours:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      'Grand Prix': '#c41e3a',
      'Croisière': '#2c5f7d',
      'Séminaire': '#8b4f6d',
      'Escapade': '#8b7355'
    };
    return colors[type] || '#666666';
  };

  const getStatusBadge = (status) => {
    if (status === 'Complet') {
      return { text: language === 'fr' ? 'Complet' : 'Sold Out', color: '#c41e3a' };
    }
    if (status === 'Annulé') {
      return { text: language === 'fr' ? 'Annulé' : 'Cancelled', color: '#999' };
    }
    return null;
  };

  // Helper to get image URL (handle both external URLs and uploaded images)
  const getImageUrl = (image) => {
    if (!image) return 'https://images.unsplash.com/photo-1616620418653-c2f1603f507d?w=600&q=80';
    if (image.startsWith('http')) return image;
    // Convert /uploads/ to /api/uploads/ for proper serving
    const apiPath = image.replace('/uploads/', '/api/uploads/');
    return `${process.env.REACT_APP_BACKEND_URL}${apiPath}`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', padding: '100px 0', textAlign: 'center' }}>
        <p className="body-regular">{language === 'fr' ? 'Chargement des séjours...' : 'Loading stays...'}</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-light)',
        padding: '32px 40px'
      }}>
        <div className="container" style={{ maxWidth: '1400px' }}>
          <Link to="/member/dashboard" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '14px'
          }}>
            <ArrowLeft size={16} />
            {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
          </Link>
          <h1 className="hero-medium">
            {language === 'fr' ? 'Séjours VIP' : 'VIP Stays'}
          </h1>
          <p className="body-regular" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
            {language === 'fr' 
              ? 'Découvrez nos séjours exclusifs sur les circuits les plus prestigieux' 
              : 'Discover our exclusive stays at the most prestigious circuits'}
          </p>
        </div>
      </div>

      {/* Stays List */}
      <section className="section-padding">
        <div className="container">
          {stays.length === 0 ? (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-light)'
            }}>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {language === 'fr' ? 'Aucun séjour disponible pour le moment' : 'No stays available at the moment'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '32px'
            }}>
              {stays.map((stay) => {
                const statusBadge = getStatusBadge(stay.status);
                const availableSeats = stay.total_seats - stay.seats_sold;
                
                return (
                  <div
                    key={stay.id}
                    className="hover-lift"
                    style={{
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-light)',
                      overflow: 'hidden',
                      position: 'relative',
                      opacity: stay.status === 'Annulé' ? 0.7 : 1
                    }}
                  >
                    {/* Stay Image */}
                    <div style={{ position: 'relative' }}>
                      <img
                        src={getImageUrl(stay.image)}
                        alt={language === 'fr' ? stay.title : (stay.title_en || stay.title)}
                        style={{
                          width: '100%',
                          height: '240px',
                          objectFit: 'cover'
                        }}
                      />
                      {/* Type Badge */}
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        padding: '6px 12px',
                        background: getTypeBadgeColor(stay.type),
                        color: 'var(--bg-primary)',
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                      }}>
                        {stay.type}
                      </div>
                      
                      {/* Status Badge */}
                      {statusBadge && (
                        <div style={{
                          position: 'absolute',
                          top: '16px',
                          left: '16px',
                          padding: '6px 12px',
                          background: statusBadge.color,
                          color: 'var(--bg-primary)',
                          fontSize: '11px',
                          fontWeight: 600,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase'
                        }}>
                          {statusBadge.text}
                        </div>
                      )}
                    </div>

                    {/* Stay Details */}
                    <div style={{ padding: '28px' }}>
                      {/* Date */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px'
                      }}>
                        <Calendar size={18} color="var(--text-secondary)" />
                        <span className="body-regular" style={{ 
                          color: 'var(--text-secondary)',
                          fontWeight: 600 
                        }}>
                          {language === 'fr' ? stay.date : (stay.date_en || stay.date)}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="heading-2" style={{ 
                        marginBottom: '12px',
                        lineHeight: 1.3
                      }}>
                        {language === 'fr' ? stay.title : (stay.title_en || stay.title)}
                      </h3>

                      {/* Location */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '16px'
                      }}>
                        <MapPin size={16} color="var(--text-secondary)" />
                        <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                          {stay.location}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="body-regular" style={{ 
                        color: 'var(--text-secondary)',
                        lineHeight: 1.7,
                        marginBottom: '20px',
                        paddingBottom: '20px',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        {language === 'fr' ? stay.description : (stay.description_en || stay.description)}
                      </p>

                      {/* Flight Info */}
                      {stay.flight_info && (
                        <div style={{
                          padding: '12px',
                          background: 'rgba(44, 95, 125, 0.05)',
                          border: '1px solid var(--border-light)',
                          marginBottom: '16px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px'
                          }}>
                            <Plane size={18} color="#2c5f7d" style={{ flexShrink: 0, marginTop: '2px' }} />
                            <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                              {stay.flight_info}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Duration & Info */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Hotel size={18} color="var(--text-secondary)" />
                          <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                            {language === 'fr' ? stay.duration : (stay.duration_en || stay.duration)}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Star size={16} color="#d4af37" fill="#d4af37" />
                          <span className="body-small" style={{ 
                            color: 'var(--text-primary)',
                            fontWeight: 600 
                          }}>
                            VIP • {availableSeats}/{stay.total_seats} {language === 'fr' ? 'places disponibles' : 'seats available'}
                          </span>
                        </div>
                      </div>

                      {/* Pricing */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '8px',
                        marginBottom: '20px',
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-light)'
                      }}>
                        <div>
                          <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {language === 'fr' ? 'Membre avec vol' : 'Member with flight'}
                          </p>
                          <p className="body-regular" style={{ fontWeight: 700, color: '#2c5f7d' }}>
                            {stay.price_member_with_flight?.toLocaleString('fr-FR')} €
                          </p>
                        </div>
                        <div>
                          <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>
                            {language === 'fr' ? 'Membre sans vol' : 'Member without flight'}
                          </p>
                          <p className="body-regular" style={{ fontWeight: 700, color: '#2c5f7d' }}>
                            {stay.price_member_no_flight?.toLocaleString('fr-FR')} €
                          </p>
                        </div>
                      </div>

                      {/* Buttons */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {stay.detail_link && (
                          <Link 
                            to={stay.detail_link}
                            className="btn-secondary" 
                            style={{ 
                              flex: 1,
                              justifyContent: 'center',
                              textDecoration: 'none',
                              display: 'flex'
                            }}
                          >
                            {language === 'fr' ? 'Détails' : 'Details'}
                          </Link>
                        )}
                        <button 
                          className="btn-primary" 
                          style={{ 
                            flex: 1,
                            justifyContent: 'center'
                          }}
                          disabled={stay.status !== 'Disponible' || availableSeats === 0}
                          onClick={() => {
                            setSelectedStay(stay);
                            setBookingModalOpen(true);
                          }}
                        >
                          {stay.status === 'Disponible' && availableSeats > 0
                            ? (language === 'fr' ? 'Réserver' : 'Book now')
                            : (language === 'fr' ? 'Complet' : 'Sold out')
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="section-padding-small" style={{ background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '900px', textAlign: 'center' }}>
          <h3 className="heading-1" style={{ marginBottom: '16px' }}>
            {language === 'fr' ? 'Séjours Exclusifs VIP' : 'Exclusive VIP Stays'}
          </h3>
          <p className="body-regular" style={{ 
            color: 'var(--text-secondary)', 
            lineHeight: 1.8 
          }}>
            {language === 'fr'
              ? 'Tous nos séjours sont réservés aux membres du Club Henry & Gaby. Chaque séjour est soigneusement organisé pour offrir une expérience inoubliable sur les circuits les plus prestigieux de Formule 1, avec transport VIP, hébergement de luxe et accès privilégiés.'
              : 'All our stays are reserved for Henry & Gaby Club members. Each stay is carefully organized to offer an unforgettable experience at the most prestigious Formula 1 circuits, with VIP transport, luxury accommodation and privileged access.'}
          </p>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedStay(null);
        }}
        item={selectedStay}
        bookingType="stay"
        onSuccess={() => {
          fetchStays(); // Refresh to update available seats
        }}
      />
    </div>
  );
};

export default Stays;
